import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { Settings, APP_CONFIG } from '../config/settings';
import { IdentityService, Identity } from './identity.service';
import { combine } from '../shared/utilities/route';
import { ToasterService } from '../shared/services/toaster.service';
import { User } from '@auth0/auth0-angular';
import { LocalStorageKeys } from './enums/storage-keys.enum';
import { WINDOW } from './core.module';
import { Router } from '@angular/router';
import { UserProfile } from './models/user-profile.model';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private identUrl: string;

    constructor(
        private readonly _http: HttpClient,
        private readonly _identityService: IdentityService,
        private readonly _router: Router,
        private readonly _toasterService: ToasterService,
        @Inject(WINDOW) private readonly _window: Window,
        @Inject(APP_CONFIG) private readonly _settings: Settings
    ) {
        this.identUrl = combine(this._settings.coreAPIUrl, 'ayanova/account/users/me');
    }

    retrieveIdentity(): Observable<string> {
        return this._http.get<Identity>(this.identUrl).pipe(
            map((identity) => {
                if (!identity.permissionsToken) {
                    throw new Error('There are no modules available for your account.');
                }
                this._identityService.set(identity);
                return '';
            }),
            catchError((error: unknown) => {
                return throwError(() => error);
            })
        );
    }

    getProfiles(): Observable<UserProfile[]> {
        //listenProfileChanged call to be moved to this AuthService
        //  Constructor *1 after feature flags cleanup - Auth0NovaLinkedProfiles, auth0NovaImpersonation
        // this._identityService.identity$.pipe(
        //                   filter((identity) => !!identity?.userId),
        //                   take(1)).subscribe((identity: Identity) => {
        const userId = this._identityService.current?.userId;
        if (userId) {
            this.listenProfileChanged(userId);
        }

        return this._http.get<UserProfile[]>(`${this._settings.coreAPIUrl}/AyaConnect/Auth/profiles`).pipe(
            catchError(() => {
                console.error('Get profiles request failed');
                return of([]);
            })
        );
    }

    changeProfile(userId: string, impersonate = false) {
        let url = `${this._settings.coreAPIUrl}/auth-user/profiles/${userId}`;
        if (impersonate) {
            url += '/impersonate';
        }
        this._http
            .put<string>(url, null, { responseType: 'text' as any })
            .pipe(
                switchMap(() => this._identityService.user$),
                take(1)
            )
            .subscribe({
                next: (u: User) => {
                    this._identityService.delete();
                    const path =
                        (u?.connection && u.connection === this._settings.authSsoConnection ? `/sso` : '/signin') +
                        '?profile-changed=true';
                    this._router.navigateByUrl(path);
                },
                error: (error) => {
                    const errorMessage =
                        parseInt(error?.status) === 403
                            ? 'Users profile status is not currently active. Please activate users profile status.'
                            : 'Impersonation failed, please contact IT support to resolve the impersonation error.';
                    this._toasterService.toastErrors([errorMessage]);
                }
            });
    }

    stopImpersonation(mainUserId: string): Observable<unknown> {
        const url = `${this._settings.coreAPIUrl}/auth-user/profiles/${mainUserId}`;
        return this._http.put<string>(url, null, { responseType: 'text' as any });
    }

    private listenProfileChanged(activeProfile: string) {
        this.removeProfileChangedListener();
        const localActiveProfile = this._window.localStorage.getItem(LocalStorageKeys.ACTIVE_PROFILE);
        if (activeProfile !== localActiveProfile) {
            this._window.localStorage.setItem(LocalStorageKeys.ACTIVE_PROFILE, activeProfile);
        }

        if (this._window.addEventListener) {
            this._window.addEventListener('storage', this._profileChangedEventHandler, false);
        }
    }

    private removeProfileChangedListener() {
        this._window.removeEventListener('storage', this._profileChangedEventHandler, false);
    }

    private readonly _profileChangedEventHandler = (e) => {
        this._profileChangedEvent(e);
    };

    private _profileChangedEvent(e) {
        if (e.key === LocalStorageKeys.ACTIVE_PROFILE && e.newValue && e.newValue !== e.oldValue) {
            this.removeProfileChangedListener();
            this._window.location.replace('/');
        }
    }
}
